import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import COButton from '../COButton';
import styles from './CreateBrandModal.module.scss';
import {
  ButtonLabels,
  CreateBrandModalProps,
  FieldLabels,
} from 'enums/CreateBrandModalEnums';
import InputField from '../InputField';
import COSelect from '../COSelect';
import { Formik, Form } from 'formik';
import { validationSchema } from './CreateBrandModalConstants';
import { MetaData } from 'models/MetaData/metaData.model';
import { modalCloseIcon } from '../OrganizationModal';
import BrandService from 'services/ProductsService/brand.service';
import EmissionService from 'services/ProductsService/emission.service';

interface CreateBrandModal {
  showModalState: boolean;
  handleModalCancel: (payload: boolean) => void;
  successCallback?: (brandId: string) => void;
  preFetchedData?: number;
  createBrand?: any;
  updateBrand?: (
    id: number,
    Brand: {
      name: string | undefined;
      category: string | undefined;
    }
  ) => Promise<void>;
}

interface CategoryOptions {
  value: string | undefined;
  label: string | undefined;
  key: string | number | undefined;
}

const convertToOptions = (data: MetaData[]) => {
  const options: CategoryOptions[] = [];
  data?.map((item: MetaData) => {
    options.push({
      label: item.name,
      value: item.name,
      key: item.id,
    });
  });
  return options;
};
const CreateBrandModal: React.FC<CreateBrandModal> = ({
  showModalState,
  handleModalCancel,
  successCallback,
  preFetchedData,
  createBrand,
  updateBrand,
}: CreateBrandModal) => {
  const { fetchMetaData, metaData } = EmissionService();
  const { loading, fetchSingleBrand, brand } = BrandService();
  const [brandCategoriesMeta, setBrandCategoriesMeta] = useState<
    CategoryOptions[]
  >([]);

  useEffect(() => {
    fetchMetaData({ metadata_type: 'brand_type' });
  }, []);

  useEffect(() => {
    const BrandOptions = metaData && convertToOptions(metaData);
    setBrandCategoriesMeta(BrandOptions);
  }, [metaData]);

  const preFetchedValue = {
    brandName: brand ? brand.name : CreateBrandModalProps.EMPTY_STRING,
    category: brand ? brand.category : undefined,
  };

  useEffect(() => {
    if (preFetchedData) {
      fetchSingleBrand(preFetchedData);
    }
  }, [preFetchedData]);

  const handleCloseModal = () => {
    handleModalCancel(false);
  };

  const handleFormSubmission = async (values: {
    brandName: string | undefined;
    category: string | undefined;
  }) => {
    try {
      let data;
      if (preFetchedData) {
        await updateBrand?.(preFetchedData, {
          name: values.brandName,
          category: values?.category,
        });
      } else {
        data = await createBrand({
          name: values.brandName,
          category: values?.category,
        });
        successCallback && successCallback(data?.id ?? 0);
      }
      handleCloseModal();
    } catch (error) {}
  };

  return (
    <Modal
      destroyOnClose={true}
      maskClosable={false}
      width={CreateBrandModalProps.MODAL_WIDTH}
      footer={null}
      wrapClassName={styles.modalContainer}
      open={showModalState}
      closeIcon={modalCloseIcon}
      onCancel={handleCloseModal}>
      <div className={styles.formContainer}>
        <div className={styles.headerContainer}>
          <h2>
            {preFetchedData
              ? CreateBrandModalProps.UPDATE_HEADING
              : CreateBrandModalProps.HEADING}
          </h2>
        </div>
        {preFetchedData && loading ? (
          <h3>{CreateBrandModalProps.LOADING_INDICATOR}</h3>
        ) : (
          <Formik
            enableReinitialize
            initialValues={preFetchedValue}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmission}>
            {formik => (
              <Form>
                <div className={styles.fieldContainer}>
                  <InputField
                    name={FieldLabels.SELECT_NAME}
                    placeholderstring={FieldLabels.SELECT_PLACEHOLDER}
                    label={FieldLabels.SELECT_LABEL}
                  />
                </div>
                <div className={styles.fieldContainer}>
                  <COSelect
                    name={FieldLabels.TEXT_NAME}
                    placeholderstring={FieldLabels.TEXT_PLACEHOLDER}
                    label={FieldLabels.TEXT_PLACEHOLDER}
                    options={brandCategoriesMeta}
                  />
                </div>
                <div className={styles.buttonContainer}>
                  <COButton submit loading={formik.isSubmitting}>
                    {preFetchedData
                      ? ButtonLabels.UPDATE
                      : ButtonLabels.CONFIRM}
                  </COButton>
                  <COButton secondary onClick={handleCloseModal}>
                    {ButtonLabels.CANCEL}
                  </COButton>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </Modal>
  );
};

export default CreateBrandModal;
